import { useSuspenseQuery } from "@tanstack/react-query";
import {
  AnalyticsScreenProvider,
  Callout,
  FloatingFilesList,
  Flex,
  Link,
  OnboardingLayout,
  Text,
} from "@wayflyer/flyui";
import { IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getStageCompletionStatusQueryOptions } from "shared-ui";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";
import { BankControllerProvider } from "../hooks/bank-controller-context";
import { useBankingAccountsModals } from "../hooks/use-banking-accounts-modals";
import { EXTRACTION_FAILED_METADATA, useBankStatementUploads } from "../hooks/use-bank-statement-uploads";
import { useBankingAccountsPageController } from "../hooks/use-banking-accounts-page-controller";
import { useBankingPageSnapshot } from "../hooks/use-banking-page-snapshot";
import { setReopenGroupIntent } from "../hooks/reopen-group-panel-storage";
import { useReassignedConnectionsForGroup } from "../hooks/use-reassigned-connections-for-group";
import { useReassignedStatementsForGroup } from "../hooks/use-reassigned-statements-for-group";
import { useReopenGroupPanelOnReturn } from "../hooks/use-reopen-group-panel-on-return";

import { AccountDetailsPanel } from "./account-details-panel/account-details-panel";
import { BankAccountsEmptyState } from "./bank-accounts-empty-state/bank-accounts-empty-state";
import { BankAccountsFilledState } from "./bank-accounts-filled-state/bank-accounts-filled-state";
import { ConfirmAccountsPanel } from "./confirm-accounts-panel/confirm-accounts-panel";
import { ConnectModal } from "./connect-modal/connect-modal";
import { GroupDetailsPanel } from "./group-details-panel/group-details-panel";
import { MESSAGES } from "./message-definitions";
import { RequirementsModal } from "./requirements-modal/requirements-modal";
import { UnmatchedStatementsPanel } from "./unmatched-statements-panel/unmatched-statements-panel";
import { findGroupByUuid, hasBankAccounts } from "./utils";

// Plaid is embedded (no redirect), unlike Basiq/Mocked/TrueLayer - this drives the reopen-intent
// and modal-close-timing branches below.
const PLAID_PROVIDER_NAME = "Plaid";

const BankingAccountsPageContent = () => {
  const intl = useIntl();
  const posthog = usePostHog();

  const {
    customerDomain,
    bankConnectProps,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
    handleNext,
    providerName,
    ready,
    isSubmitting,
    requiredStatementMonths,
    requireConnection,
    isUkIncorporated,
    isConnectionsSlideoutOpen,
    closeConnectionsSlideout,
    shouldConfirmAccountSelection,
    confirmAccountsBankConnections,
    accountGroups,
    ungroupedConnections,
    ungroupedStatements,
    hasAnyBankingConnection,
    hasProvidedBankingData,
    logoLoadingStates,
    invalidateBankingSources,
    invalidateAllBankingSources,
    isRefetchingBankingSources,
  } = useBankingAccountsPageController();

  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.BANKING });
  const applicationSubmit = useApplicationSubmit({ currentPage: PAGE_TYPE.BANKING });

  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));
  const tradingHistoryThresholdMonths = stageCompletionStatus.timeInBusinessThresholdMonths ?? 6;
  const isLowTimeInBusiness = stageCompletionStatus.isLowTimeInBusiness === true;

  const modals = useBankingAccountsModals();

  const { selectedGroup: selectedGroupSnapshot } = modals;
  const selectedGroup = selectedGroupSnapshot
    ? findGroupByUuid(accountGroups, selectedGroupSnapshot.uuid) ?? selectedGroupSnapshot
    : null;

  // useReassignedConnectionsForGroup below depends on pendingMemberUuidsBeforeConnect being read
  // here first, in this render - it seeds its own snapshot from it exactly once, on mount.
  const { pendingMemberUuidsBeforeConnect } = useReopenGroupPanelOnReturn({
    customerDomain,
    accountGroups,
    invalidateBankingSources,
    onSelectGroup: modals.onSelectGroup,
    isConfirmAccountSelectionPending: shouldConfirmAccountSelection && confirmAccountsBankConnections.length > 0,
    isGroupDetailsPanelOpen: modals.isGroupDetailsPanelOpen,
  });

  const reassignedConnections = useReassignedConnectionsForGroup({
    accountGroups,
    originGroupUuid: selectedGroup?.uuid ?? null,
    persistedMemberUuidsBeforeConnect: pendingMemberUuidsBeforeConnect,
  });

  // Fires once per member the moment it's first seen as reassigned, not on every re-render.
  const trackedReassignedMemberUuids = useRef(new Set<string>());
  useEffect(() => {
    for (const { member, destinationGroup } of reassignedConnections.reassignedMembers) {
      if (trackedReassignedMemberUuids.current.has(member.memberUuid)) {
        continue;
      }
      trackedReassignedMemberUuids.current.add(member.memberUuid);
      posthog?.capture("banking_connection_completed", {
        outcome: "reassigned",
        origin_group_uuid: selectedGroup?.uuid ?? null,
        destination_group_uuid: destinationGroup.uuid,
      });
    }
  }, [reassignedConnections.reassignedMembers, posthog, selectedGroup?.uuid]);

  const reassignedStatements = useReassignedStatementsForGroup({
    accountGroups,
    originGroupUuid: selectedGroup?.uuid ?? null,
  });

  const statements = useBankStatementUploads({
    customerDomain,
    requiredStatementMonths,
    onUploadsOrExtractionSettled: invalidateBankingSources,
  });

  const hasActiveUpload = statements.uploadsState.uploads.some((upload) =>
    ["queued", "uploading", "processing"].includes(upload.status)
  );
  useBankingPageSnapshot({
    accountGroups,
    ungroupedConnections,
    requiredStatementMonths,
    isSettled: !isRefetchingBankingSources && !hasActiveUpload,
  });

  const onFilesAdded: typeof statements.onFilesAdded = (files, rejectedFiles) => {
    statements.registerFilesForBatch(files, "main_page");
    modals.setIsFloatingFilesListOpen(true);
    statements.onFilesAdded(files, rejectedFiles);
  };

  const onFileRemoved: typeof statements.onFileRemoved = async (file, uploadedFile) => {
    await statements.onFileRemoved(file, uploadedFile);
    invalidateBankingSources();
  };

  const onPanelFilesAdded: typeof statements.onFilesAdded = (files, rejectedFiles) => {
    if (selectedGroup && files.length > 0) {
      reassignedStatements.recordUploadAttempt();
      statements.registerFilesForGroup(files, selectedGroup.uuid);
    }
    statements.registerFilesForBatch(files, "side_panel");
    statements.onFilesAdded(files, rejectedFiles);
  };

  const isEmpty =
    accountGroups.length === 0 &&
    ungroupedConnections.length === 0 &&
    ungroupedStatements.length === 0 &&
    !statements.hasAcceptedUploadThisSession;
  const isUploadDisabledPendingConnection = requireConnection && !hasAnyBankingConnection;
  const isStatementsFullScreenDropzoneEnabled = !modals.isGroupDetailsPanelOpen;
  const floatingFilesListSubtitle =
    statements.uploadsState.uploads.length > 0
      ? intl.formatMessage(MESSAGES.floatingFilesListProgress, {
          done: statements.uploadsState.uploads.filter((upload) => ["complete", "failed"].includes(upload.status))
            .length,
          total: statements.uploadsState.uploads.length,
        })
      : undefined;

  const openConnectModal = () => modals.setIsConnectModalOpen(true);

  const onConnectInstantlyForGroup = () => {
    posthog?.capture("banking_connection_started", { entry_point: "group_panel" });
    openConnectModal();
  };

  const onContinueConnect = async () => {
    const isGroupPanelConnect = modals.isGroupDetailsPanelOpen && selectedGroup;
    const memberUuidsBeforeConnect = isGroupPanelConnect ? reassignedConnections.recordConnectAttempt() : null;

    const started = await handleConnect();

    if (!started) {
      if (isGroupPanelConnect) {
        reassignedConnections.clear();
      }
      return;
    }

    if (isGroupPanelConnect && providerName !== PLAID_PROVIDER_NAME) {
      // Plaid never redirects, so it has no need to survive a reload.
      setReopenGroupIntent(customerDomain, selectedGroup.uuid, memberUuidsBeforeConnect ?? []);
    }
    if (providerName === PLAID_PROVIDER_NAME) {
      modals.setIsConnectModalOpen(false);
    }
  };

  const onNext = () => handleNext(() => applicationSubmit.submitApplication());

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.pageTitle)}
        subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
        footer={
          isUkIncorporated &&
          hasProvidedBankingData && (
            <Text size="xs">
              <FormattedMessage
                {...MESSAGES.ukBankingSatisfiedDisclosure}
                values={{
                  nextStep: navigationContext.nextButtonLabel,
                  privacyLink: (chunks: ReactNode) => (
                    <Link url="https://www.wayflyer.com/privacy-notice" external>
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Text>
          )
        }
        actions={{
          primary: {
            content: navigationContext.nextButtonLabel,
            onAction: onNext,
            disabled: statements.uploadsInProgress || !ready,
            loading: isSubmitting || applicationSubmit.isSubmitting,
            icon: IconArrowRight24Line,
            analyticsId: "banking-accounts-page-next-button",
          },
          // TODO: Add back button when business model can be recalculated
          // secondary: {
          //   content: navigationContext.backButtonLabel,
          //   icon: IconArrowLeft24Line,
          //   onAction: handleBack,
          //   analyticsId: "banking-accounts-page-back-button",
          // },
        }}
      >
        {isLowTimeInBusiness && (
          <Callout
            variant="warning"
            description={intl.formatMessage(MESSAGES.timeInBusinessBanner, {
              months: tradingHistoryThresholdMonths,
              b: (chunks: ReactNode) => (
                <Text element="span" fontWeight="semiBold">
                  {chunks}
                </Text>
              ),
            })}
          />
        )}
        {isEmpty ? (
          <BankAccountsEmptyState
            bankConnectProps={bankConnectProps}
            countryForm={countryForm}
            countryOptions={countryOptions}
            defaultCountry={defaultCountry}
            isConnectionUnsupportedCountry={isConnectionUnsupportedCountry}
            isConnecting={isConnecting}
            ready={ready}
            requireConnection={requireConnection}
            isUploadDisabledPendingConnection={isUploadDisabledPendingConnection}
            isStatementsFullScreenDropzoneEnabled={isStatementsFullScreenDropzoneEnabled}
            onConnect={handleConnect}
            onFilesAdded={onFilesAdded}
          />
        ) : (
          <BankAccountsFilledState
            accountGroups={accountGroups}
            ungroupedConnections={ungroupedConnections}
            ungroupedStatements={ungroupedStatements}
            logoLoadingStates={logoLoadingStates}
            requiredStatementMonths={requiredStatementMonths}
            onSelectAccount={modals.onSelectAccount}
            onSelectGroup={modals.onSelectGroup}
            onSelectUnmatchedStatements={() => modals.setIsUnmatchedStatementsPanelOpen(true)}
            onOpenConnectModal={openConnectModal}
            onFilesAdded={onFilesAdded}
            isStatementsFullScreenDropzoneEnabled={isStatementsFullScreenDropzoneEnabled}
          />
        )}

        <Flex direction="column" gap="1">
          <Text fontWeight="semiBold" size="xs">
            {intl.formatMessage(MESSAGES.requiredFromYouHeading)}
          </Text>
          <Text size="xs">
            <FormattedMessage
              {...MESSAGES.requiredFromYouBodyText}
              values={{
                startDate: statements.statementPeriodStart,
                viewMore: (
                  <Link onClick={() => modals.setIsRequirementsModalOpen(true)}>
                    {intl.formatMessage(MESSAGES.viewMore)}
                  </Link>
                ),
              }}
            />
          </Text>
        </Flex>
      </OnboardingLayout>

      <FloatingFilesList
        defaultExpanded
        isOpen={modals.isFloatingFilesListOpen}
        labels={{
          title: intl.formatMessage(MESSAGES.floatingFilesListTitle),
          subtitle: floatingFilesListSubtitle,
          failedFileMessage: intl.formatMessage(MESSAGES.uploadFailedMessage),
          cancelAction: intl.formatMessage(MESSAGES.cancelAction),
          retryAction: intl.formatMessage(MESSAGES.retryAction),
          retryAllAction: intl.formatMessage(MESSAGES.retryAllAction),
          doneAction: intl.formatMessage(MESSAGES.doneAction),
        }}
        batches={[
          {
            id: "banking-accounts-statements",
            title: intl.formatMessage(MESSAGES.floatingFilesListTitle),
            uploadsState: statements.uploadsState,
            onFileRemoved,
            onFileRetry: statements.onFileRetry,
            canRetryFile: (upload) => upload.metadata !== EXTRACTION_FAILED_METADATA,
            onDone: () => modals.setIsFloatingFilesListOpen(false),
          },
        ]}
      />

      <AccountDetailsPanel
        isOpen={modals.isAccountDetailsPanelOpen}
        selectedAccount={modals.selectedAccount}
        onClose={() => modals.setIsAccountDetailsPanelOpen(false)}
        logoLoadingStates={logoLoadingStates}
      />

      <ConfirmAccountsPanel
        isOpen={isConnectionsSlideoutOpen && shouldConfirmAccountSelection && confirmAccountsBankConnections.length > 0}
        onClose={closeConnectionsSlideout}
        bankConnections={confirmAccountsBankConnections}
      />

      <GroupDetailsPanel
        key={selectedGroup?.uuid}
        isOpen={modals.isGroupDetailsPanelOpen}
        group={selectedGroup}
        bankConnectProps={bankConnectProps}
        accountGroups={accountGroups}
        companyDomain={customerDomain}
        logoLoadingStates={logoLoadingStates}
        requiredStatementMonths={requiredStatementMonths}
        reassignedMembers={reassignedConnections.reassignedMembers}
        reassignedStatementMembers={reassignedStatements.reassignedMembers}
        onClose={() => {
          modals.setIsGroupDetailsPanelOpen(false);
          reassignedConnections.clear();
          reassignedStatements.clear();
        }}
        onCloseWithActiveUploads={() => modals.setIsFloatingFilesListOpen(true)}
        onConnect={onConnectInstantlyForGroup}
        onFilesAdded={onPanelFilesAdded}
        onFileRetry={statements.onFileRetry}
        uploads={statements.uploads}
        uploadsState={statements.uploadsState}
        isRefetchingBankingSources={isRefetchingBankingSources}
        onStatementsChanged={invalidateAllBankingSources}
      />

      <UnmatchedStatementsPanel
        isOpen={modals.isUnmatchedStatementsPanelOpen}
        statements={ungroupedStatements}
        accountGroups={accountGroups}
        companyDomain={customerDomain}
        hasBankAccounts={hasBankAccounts(accountGroups, ungroupedConnections)}
        onClose={() => modals.setIsUnmatchedStatementsPanelOpen(false)}
        onStatementsChanged={invalidateAllBankingSources}
      />

      <ConnectModal
        isOpen={modals.isConnectModalOpen}
        onClose={() => modals.setIsConnectModalOpen(false)}
        onContinue={onContinueConnect}
        countryForm={countryForm}
        countryOptions={countryOptions}
        defaultCountry={defaultCountry}
        isConnectionUnsupportedCountry={isConnectionUnsupportedCountry}
        isConnecting={isConnecting}
        ready={ready}
      />

      <RequirementsModal
        isOpen={modals.isRequirementsModalOpen}
        onClose={() => modals.setIsRequirementsModalOpen(false)}
      />
    </>
  );
};

export const BankingAccountsPage = () => (
  <AnalyticsScreenProvider screen="onboarding-banking-accounts">
    <BankControllerProvider isNewAccountsPageActive>
      <BankingAccountsPageContent />
    </BankControllerProvider>
  </AnalyticsScreenProvider>
);
